<template>
  <div class="ux-datepicker" ref="container">
    <div class="ux-datepicker-input-wrap">
      <input
        type="text"
        class="ux-datepicker-input"
        ref="input"
        v-model="displayText"
        :placeholder="placeholder"
        @blur="commitValue"
        @keydown.enter.prevent="commitValue"
        @keydown.escape.prevent="closeMenu"
      />
      <span class="ux-datepicker-icon" @click.stop="toggleMenu">
        <ux-icon icon="fa-calendar" />
      </span>
    </div>
    <ux-menu ref="menu" v-model="menuOpen" :closeOnClick="false">
      <template #activator><span></span></template>
      <div class="ux-datepicker-popup" @click.stop>
        <div class="ux-datepicker-calendar">
          <div class="ux-datepicker-header">
            <button type="button" class="ux-datepicker-nav" @click="prevMonth">
              <ux-icon icon="fa-chevron-left" />
            </button>
            <span class="ux-datepicker-month-year">
              {{ monthNames[viewMonth] }} {{ viewYear }}
            </span>
            <button type="button" class="ux-datepicker-nav" @click="nextMonth">
              <ux-icon icon="fa-chevron-right" />
            </button>
          </div>
          <div class="ux-datepicker-weekdays">
            <span v-for="day in weekdayNames" :key="day">{{ day }}</span>
          </div>
          <div class="ux-datepicker-days">
            <button
              type="button"
              v-for="(day, index) in calendarDays"
              :key="index"
              class="ux-datepicker-day"
              :class="{
                'other-month': day.otherMonth,
                today: day.isToday,
                selected: day.isSelected,
                disabled: day.isDisabled,
              }"
              :disabled="day.isDisabled"
              @click="selectDate(day)"
            >
              {{ day.date }}
            </button>
          </div>
        </div>
        <div class="ux-datepicker-time" v-if="!dateOnly">
          <flex-row style="justify-content: center">
            <flex-cell shrink>
              <native-select
                :modelValue="selectedHour"
                @update:modelValue="onHourChange"
                :field="{ minimum: 1, maximum: 1, options: hourOptions }"
              >
                <div class="time-section">
                  {{ displayHour }}
                  <ux-icon right icon="fa-angle-down" />
                </div>
              </native-select>
            </flex-cell>
            <flex-cell shrink>
              <div class="time-section time-separator">:</div>
            </flex-cell>
            <flex-cell shrink>
              <native-select
                :modelValue="selectedMinute"
                @update:modelValue="onMinuteChange"
                :field="{ minimum: 1, maximum: 1, options: minuteOptions }"
              >
                <div class="time-section">
                  {{ displayMinute }}
                  <ux-icon right icon="fa-angle-down" />
                </div>
              </native-select>
            </flex-cell>
            <flex-cell shrink>
              <native-select
                :modelValue="selectedMeridian"
                @update:modelValue="onMeridianChange"
                :field="{ minimum: 1, maximum: 1, options: meridianOptions }"
              >
                <div class="time-section">
                  {{ selectedMeridian }}
                  <ux-icon right icon="fa-angle-down" />
                </div>
              </native-select>
            </flex-cell>
          </flex-row>
        </div>
      </div>
    </ux-menu>
  </div>
</template>

<script>
import * as chrono from "chrono-node";
import { DateTime } from "luxon";
import NativeSelect from "./select.vue";

function zero(input) {
  if (String(input).length < 2) {
    input = `0${input}`;
  }
  return input;
}

function getChronoParser() {
  const locale = navigator.language || "en-US";
  if (locale.startsWith("en-AU") || locale.startsWith("en-GB")) {
    return chrono.en.GB;
  }
  return chrono.en;
}

export default {
  components: {
    NativeSelect,
  },
  props: {
    modelValue: {
      type: [Date, String, Object],
      default: null,
    },
    dateOnly: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: [Date, String],
      default: null,
    },
    maxDate: {
      type: [Date, String],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Enter a date...",
    },
  },
  emits: ["update:modelValue"],
  data() {
    // Create internal copy of the date - never mutate the prop
    const initialDate = this.modelValue ? new Date(this.modelValue) : null;
    return {
      internalDate: initialDate,
      displayText: "",
      menuOpen: false,
      viewMonth: initialDate ? initialDate.getMonth() : new Date().getMonth(),
      viewYear: initialDate
        ? initialDate.getFullYear()
        : new Date().getFullYear(),
      selectedHour: 12,
      selectedMinute: 0,
      selectedMeridian: "PM",
      parseTimeout: null,
      weekdayNames: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
  },
  computed: {
    parsedModelValue() {
      if (!this.modelValue) return null;
      const d = new Date(this.modelValue);
      return isNaN(d.getTime()) ? null : d;
    },
    minDateParsed() {
      if (!this.minDate) return null;
      const d = new Date(this.minDate);
      return isNaN(d.getTime()) ? null : d;
    },
    maxDateParsed() {
      if (!this.maxDate) return null;
      const d = new Date(this.maxDate);
      return isNaN(d.getTime()) ? null : d;
    },
    calendarDays() {
      const days = [];
      const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
      const lastOfMonth = new Date(this.viewYear, this.viewMonth + 1, 0);
      const startDay = firstOfMonth.getDay(); // 0 = Sunday
      const daysInMonth = lastOfMonth.getDate();

      // Previous month days
      const prevMonth = new Date(this.viewYear, this.viewMonth, 0);
      const prevMonthDays = prevMonth.getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        const date = prevMonthDays - i;
        const fullDate = new Date(this.viewYear, this.viewMonth - 1, date);
        days.push(this.createDayObject(fullDate, date, true));
      }

      // Current month days
      for (let date = 1; date <= daysInMonth; date++) {
        const fullDate = new Date(this.viewYear, this.viewMonth, date);
        days.push(this.createDayObject(fullDate, date, false));
      }

      // Next month days to fill grid (6 rows * 7 days = 42)
      const remaining = 42 - days.length;
      for (let date = 1; date <= remaining; date++) {
        const fullDate = new Date(this.viewYear, this.viewMonth + 1, date);
        days.push(this.createDayObject(fullDate, date, true));
      }

      return days;
    },
    displayHour() {
      let h = this.selectedHour;
      if (h === 0) h = 12;
      return h;
    },
    displayMinute() {
      return zero(this.selectedMinute);
    },
    hourOptions() {
      return [
        { title: "12", value: 0 },
        { title: "1", value: 1 },
        { title: "2", value: 2 },
        { title: "3", value: 3 },
        { title: "4", value: 4 },
        { title: "5", value: 5 },
        { title: "6", value: 6 },
        { title: "7", value: 7 },
        { title: "8", value: 8 },
        { title: "9", value: 9 },
        { title: "10", value: 10 },
        { title: "11", value: 11 },
      ];
    },
    minuteOptions() {
      const options = [];
      for (let m = 0; m < 60; m += 5) {
        options.push({ title: zero(m), value: m });
      }
      return options;
    },
    meridianOptions() {
      return [
        { title: "AM", value: "AM" },
        { title: "PM", value: "PM" },
      ];
    },
    dateAsString() {
      if (!this.internalDate) return null;
      const d = new Date(this.internalDate);
      d.setSeconds(0, 0); // Normalize - zero out seconds and milliseconds
      return d.toISOString();
    },
    modelValueAsString() {
      if (!this.modelValue) return null;
      const d = new Date(this.modelValue);
      if (isNaN(d.getTime())) return null;
      d.setSeconds(0, 0); // Same normalization
      return d.toISOString();
    },
  },
  watch: {
    dateAsString(newVal) {
      if (newVal !== this.modelValueAsString) {
        this.$emit("update:modelValue", newVal);
      }
    },
    modelValue: {
      immediate: true,
      handler(val) {
        if (val) {
          // Always create a NEW Date object - never reference the prop
          const d = new Date(val);
          if (!isNaN(d.getTime())) {
            this.internalDate = d;
            this.displayText = this.formatDateForDisplay(d);
            this.viewMonth = d.getMonth();
            this.viewYear = d.getFullYear();
            this.syncTimeFromDate(d);
          }
        } else {
          this.internalDate = null;
          this.displayText = "";
        }
      },
    },
    displayText(val) {
      this.debouncedParse(val);
    },
  },
  methods: {
    createDayObject(fullDate, date, otherMonth) {
      const today = new Date();
      const isToday =
        fullDate.getDate() === today.getDate() &&
        fullDate.getMonth() === today.getMonth() &&
        fullDate.getFullYear() === today.getFullYear();

      let isSelected = false;
      if (this.parsedModelValue) {
        isSelected =
          fullDate.getDate() === this.parsedModelValue.getDate() &&
          fullDate.getMonth() === this.parsedModelValue.getMonth() &&
          fullDate.getFullYear() === this.parsedModelValue.getFullYear();
      }

      let isDisabled = false;
      if (this.minDateParsed) {
        const minDay = new Date(this.minDateParsed);
        minDay.setHours(0, 0, 0, 0);
        const checkDay = new Date(fullDate);
        checkDay.setHours(0, 0, 0, 0);
        if (checkDay < minDay) isDisabled = true;
      }
      if (this.maxDateParsed) {
        const maxDay = new Date(this.maxDateParsed);
        maxDay.setHours(23, 59, 59, 999);
        const checkDay = new Date(fullDate);
        checkDay.setHours(12, 0, 0, 0);
        if (checkDay > maxDay) isDisabled = true;
      }

      return {
        date,
        fullDate,
        otherMonth,
        isToday,
        isSelected,
        isDisabled,
      };
    },
    formatDateForDisplay(date) {
      if (!date) return "";
      const options = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      if (!this.dateOnly) {
        options.hour = "numeric";
        options.minute = "2-digit";
      }
      return new Intl.DateTimeFormat(
        navigator.language || "en-US",
        options,
      ).format(date);
    },
    debouncedParse(val) {
      if (this.parseTimeout) {
        clearTimeout(this.parseTimeout);
      }
      this.parseTimeout = setTimeout(() => {
        this.parseInput(val);
      }, 300);
    },
    parseInput(val) {
      if (!val || !val.trim()) {
        return;
      }
      const parser = getChronoParser();
      const results = parser.parse(val);
      if (results && results.length > 0) {
        const result = results[0];
        const parsed = result.start.date();

        this.viewMonth = parsed.getMonth();
        this.viewYear = parsed.getFullYear();

        // Update time if chrono parsed a time component
        if (!this.dateOnly && result.start.isCertain("hour")) {
          this.syncTimeFromDate(parsed);
        }
      }
    },
    commitValue() {
      const text = this.displayText?.trim();
      if (!text) {
        this.internalDate = null;
        return;
      }

      const parser = getChronoParser();
      const results = parser.parse(text);
      if (results && results.length > 0) {
        const result = results[0];
        const parsed = result.start.date();

        if (!this.dateOnly) {
          // If chrono parsed a time, use it and sync the controls
          if (result.start.isCertain("hour")) {
            this.syncTimeFromDate(parsed);
          } else {
            // No time in input, use the time controls
            let hour = this.selectedHour;
            if (this.selectedMeridian === "PM" && hour !== 12) {
              hour += 12;
            } else if (this.selectedMeridian === "AM" && hour === 12) {
              hour = 0;
            }
            parsed.setHours(hour, this.selectedMinute, 0, 0);
          }
        } else {
          parsed.setHours(0, 0, 0, 0);
        }
        this.internalDate = parsed;
        this.displayText = this.formatDateForDisplay(parsed);
      } else {
        // Revert to last valid value
        if (this.internalDate) {
          this.displayText = this.formatDateForDisplay(this.internalDate);
        } else {
          this.displayText = "";
        }
      }
    },
    selectDate(day) {
      if (day.isDisabled) return;
      const selected = new Date(day.fullDate);

      // Preserve time
      if (!this.dateOnly) {
        let hour = this.selectedHour;
        if (this.selectedMeridian === "PM" && hour !== 12) {
          hour += 12;
        } else if (this.selectedMeridian === "AM" && hour === 12) {
          hour = 0;
        }
        selected.setHours(hour, this.selectedMinute, 0, 0);
      } else {
        selected.setHours(0, 0, 0, 0);
      }

      this.internalDate = selected;
      this.displayText = this.formatDateForDisplay(selected);

      if (this.dateOnly) {
        this.closeMenu();
      }
    },
    syncTimeFromDate(date) {
      let hour = date.getHours();
      let meridian = "AM";
      if (hour >= 12) {
        meridian = "PM";
        if (hour > 12) hour -= 12;
      }
      if (hour === 0) hour = 12;

      // Map to our hour values (0 = 12, 1-11 = 1-11)
      this.selectedHour = hour === 12 ? 0 : hour;
      this.selectedMinute = Math.floor(date.getMinutes() / 5) * 5; // Round to nearest 5
      this.selectedMeridian = meridian;
    },
    onHourChange(val) {
      this.selectedHour = val;
      this.updateTimeValue();
    },
    onMinuteChange(val) {
      this.selectedMinute = val;
      this.updateTimeValue();
    },
    onMeridianChange(val) {
      this.selectedMeridian = val;
      this.updateTimeValue();
    },
    updateTimeValue() {
      if (!this.internalDate || this.dateOnly) return;

      const updated = new Date(this.internalDate);
      let hour = this.selectedHour;
      if (this.selectedMeridian === "PM" && hour !== 12) {
        hour += 12;
      } else if (this.selectedMeridian === "AM" && hour === 12) {
        hour = 0;
      }
      if (this.selectedHour === 0) {
        hour = this.selectedMeridian === "PM" ? 12 : 0;
      }
      updated.setHours(hour, this.selectedMinute, 0, 0);

      this.internalDate = updated;
      this.displayText = this.formatDateForDisplay(updated);
    },
    openMenu() {
      this.menuOpen = true;
    },
    closeMenu() {
      this.menuOpen = false;
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) {
        this.$refs.input?.focus();
      }
    },
    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },
    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },
  },
  beforeUnmount() {
    if (this.parseTimeout) {
      clearTimeout(this.parseTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
.ux-datepicker {
  position: relative;
  width: 100%;
}

.ux-datepicker-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.ux-datepicker-input {
  width: 100%;
  border-radius: 0.1em;
  padding: 0.5em;
  padding-right: 2.5em;
  box-sizing: border-box;
  font-size: inherit;
  color: inherit;
  appearance: none;
  font-family: inherit;
  line-height: 1;
  height: 2.5em;

  @extend .basic-input-style;

  &:focus {
    background: none;
    border: 1px solid var(--primary);
    outline: none;
  }
}

.ux-datepicker-icon {
  position: absolute;
  right: 0.5em;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  opacity: 0.5;
  padding: 0.25em;

  &:hover {
    opacity: 0.8;
  }
}

.ux-datepicker-popup {
  background: var(--bg1, #fff);
  border-radius: 0.25em;
  padding: 0.75em;
  min-width: 280px;
}

.ux-datepicker-calendar {
  user-select: none;
}

.ux-datepicker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75em;
}

.ux-datepicker-month-year {
  font-weight: 600;
  font-size: 1em;
}

.ux-datepicker-nav {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.4em;
  border-radius: 0.2em;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: color-mix(in lab, currentColor 10%, transparent);
  }
}

.ux-datepicker-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.75em;
  opacity: 0.6;
  margin-bottom: 0.5em;

  span {
    padding: 0.25em;
  }
}

.ux-datepicker-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.ux-datepicker-day {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.2em;
  color: inherit;
  padding: 0;

  &:hover:not(.disabled) {
    background: color-mix(in lab, currentColor 10%, transparent);
  }

  &.other-month {
    opacity: 0.35;
  }

  &.today {
    font-weight: 700;
    text-decoration: underline;
  }

  &.selected {
    background: var(--primary, blue);
    color: #fff;
  }

  &.disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
}

.ux-datepicker-time {
  margin-top: 0.75em;
  padding-top: 0.75em;
  border-top: 1px solid color-mix(in lab, currentColor 15%, transparent);
}

.time-section {
  padding: 0.5em 0.6em;
  background: color-mix(in lab, currentColor 8%, transparent);
  min-width: 0.5em;
  text-align: center;
  border-radius: 0.2em;
  cursor: pointer;

  .ux-icon {
    opacity: 0.3;
    font-size: 0.6em;
    margin-left: 0.25em;
  }

  &:hover {
    background: color-mix(in lab, currentColor 12%, transparent);
  }

  &.time-separator {
    background: none;
    cursor: default;
    padding-left: 0.2em;
    padding-right: 0.2em;
  }
}

.native-select:focus-within .time-section {
  outline: 1px solid var(--primary);
}
</style>
